import { CartLinesUpdateEvent } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

(() => {
  if (window.__rdBowlCartSyncApplied) return;
  window.__rdBowlCartSyncApplied = true;

  const CART_DRAWER_SECTION_ID = 'cart-drawer-section';
  const TOAST_ID = 'rd-checkout-toast';
  const TOAST_VISIBLE_MS = 6000;
  let toastTimer = null;

  // --- Cart drawer content refresh -----------------------------------------
  const refreshCartDrawer = () => {
    sectionRenderer.renderSection(CART_DRAWER_SECTION_ID, { cache: false }).catch((error) => {
      console.warn('[raw-delights-bowl-cart-sync] cart drawer refresh failed:', error);
    });
  };

  // --- Floating "Checkout now" toast ---------------------------------------
  const TRUCK_ICON_SVG = '<svg class="rd-checkout-toast__truck" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1" y="6" width="14" height="10"></rect><path d="M15 9h4l3 3v4h-7z"></path><circle cx="5.5" cy="18" r="1.8"></circle><circle cx="18" cy="18" r="1.8"></circle></svg>';

  const ensureToastElement = () => {
    let toast = document.getElementById(TOAST_ID);
    if (toast) return toast;

    toast = document.createElement('div');
    toast.id = TOAST_ID;
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <span class="rd-checkout-toast__message"></span>
      <span class="rd-checkout-toast__ready">${TRUCK_ICON_SVG}Ready to ship ✔️</span>
      <a class="rd-checkout-toast__cta" href="/checkout">Checkout now</a>
      <button class="rd-checkout-toast__dismiss" type="button" aria-label="Dismiss">&times;</button>
    `;
    document.body.appendChild(toast);

    toast.querySelector('.rd-checkout-toast__dismiss')?.addEventListener('click', () => {
      hideToast();
    });

    if (!document.getElementById('rd-checkout-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'rd-checkout-toast-styles';
      // Note: .rd-ov-mobile-primary-cta (the "Fill your bowl" sticky button from
      // raw-delights-overlays.css) occupies the full-width bottom strip on mobile
      // (left:12px, right:12px, bottom:12px, ~54px tall). The mobile rule below
      // sits clear above it instead of overlapping it, and drops max-width so
      // left+right aren't fighting a competing width constraint.
      style.textContent = `
        #${TOAST_ID} {
          position: fixed;
          left: 50%;
          bottom: 20px;
          z-index: 9999;
          display: flex;
          align-items: center;
          gap: 14px;
          max-width: calc(100% - 32px);
          padding: 14px 16px 14px 20px;
          border-radius: 999px;
          background: #24382E;
          color: #F7F3EA;
          box-shadow: 0 12px 32px rgba(20,31,25,.32);
          font-family: "Manrope", Arial, sans-serif;
          font-size: 14px;
          transform: translate(-50%, 140%);
          opacity: 0;
          transition: transform 320ms cubic-bezier(.22,1,.36,1), opacity 260ms ease;
          pointer-events: none;
        }
        #${TOAST_ID}.is-visible {
          transform: translate(-50%, 0);
          opacity: 1;
          pointer-events: auto;
        }
        #${TOAST_ID} .rd-checkout-toast__message {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: 700;
        }
        #${TOAST_ID} .rd-checkout-toast__ready {
          display: none;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
          font-size: 12px;
          font-weight: 700;
          color: #C9E4B8;
          opacity: 0.92;
        }
        #${TOAST_ID} .rd-checkout-toast__truck {
          width: 16px;
          height: 16px;
          flex: 0 0 auto;
          color: #C9E4B8;
        }
        #${TOAST_ID} .rd-checkout-toast__cta {
          flex: 0 0 auto;
          padding: 8px 16px;
          border-radius: 999px;
          background: #B9684B;
          color: #fff;
          font-weight: 800;
          text-decoration: none;
          white-space: nowrap;
        }
        #${TOAST_ID} .rd-checkout-toast__cta:hover { background: #9f5039; }
        #${TOAST_ID} .rd-checkout-toast__dismiss {
          flex: 0 0 auto;
          width: 26px;
          height: 26px;
          display: grid;
          place-items: center;
          padding: 0;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #F7F3EA;
          font-size: 18px;
          line-height: 1;
          cursor: pointer;
        }
        #${TOAST_ID} .rd-checkout-toast__dismiss:hover { background: rgba(255,255,255,.14); }
        @media (max-width: 480px) {
          #${TOAST_ID} {
            left: 12px;
            right: 12px;
            max-width: none;
            bottom: calc(84px + env(safe-area-inset-bottom));
            transform: translateY(140%);
            justify-content: flex-end;
          }
          #${TOAST_ID}.is-visible { transform: translateY(0); }
          #${TOAST_ID} .rd-checkout-toast__message { display: none; }
          #${TOAST_ID} .rd-checkout-toast__ready { display: inline-flex; }
        }
      `;
      document.head.appendChild(style);
    }

    return toast;
  };

  const hideToast = () => {
    const toast = document.getElementById(TOAST_ID);
    if (toast) toast.classList.remove('is-visible');
    if (toastTimer) {
      window.clearTimeout(toastTimer);
      toastTimer = null;
    }
  };

  const showCheckoutToast = (productTitle) => {
    const toast = ensureToastElement();
    const message = toast.querySelector('.rd-checkout-toast__message');
    if (message) {
      message.textContent = productTitle ? `Added ${productTitle} to your bowl` : 'Added to your bowl';
    }

    if (toastTimer) window.clearTimeout(toastTimer);
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');

    toastTimer = window.setTimeout(hideToast, TOAST_VISIBLE_MS);
  };

  document.addEventListener('cart:updated', (event) => {
    const detail = event.detail || {};
    const cart = detail.cart;
    const item = detail.item;

    if (!cart) return;

    let lines = [];
    if (item && item.variant_id && item.quantity) {
      lines = [
        {
          merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity: item.quantity,
        },
      ];
    }

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'raw-delights-bowl-favourites',
            sourceId: detail.sectionId || 'raw-delights-bowl-favourites',
            itemCount: cart.item_count,
            didError: false,
          },
        }),
      })
    );

    refreshCartDrawer();
    showCheckoutToast(item && item.product_title);
  });
})();
